import { useCallback, useEffect, useRef } from 'react';
import { usePage } from '@inertiajs/react';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { extractApiErrorMessage } from '@/lib/http';
import { type ActivePageTypeView, useNestedPageViewStore } from '@/stores/nested-page-view-store';
import type { Page, PageType } from '@/types/page';
import {
  CHILD_TO_INDEX_TYPE,
  createDefaultPaginationInfo,
  DEFAULT_PAGE,
  DEFAULT_PER_PAGE,
  fetchIndexPageChildren,
  findPageByType,
  getIndexPageLabel,
  hasNestedPageView,
  indexPageFromEditorPage,
  isIndexPageType,
} from './page-utils';

interface UsePagePaginationProps {
  isSidebarActive: boolean;
  hasLoaded: boolean;
  mainNavigationTree: Page[];
  nonNavigationTree: Page[];
}

interface UsePagePaginationReturn {
  activeNestedView: ActivePageTypeView | null;
  handleBackToPages: () => void;
  handlePageClick: (page: Page) => void;
  handleLoadMorePages: () => void | Promise<void>;
  handleSearchPages: (searchQuery: string) => void;
}

export function usePagePagination({
  isSidebarActive,
  hasLoaded,
  mainNavigationTree,
  nonNavigationTree,
}: UsePagePaginationProps): UsePagePaginationReturn {
  const page = usePage();
  const { activeNestedView, setActiveNestedView, appendPages } = useNestedPageViewStore(
    useShallow((state) => ({
      activeNestedView: state.activeNestedView,
      setActiveNestedView: state.setActiveNestedView,
      appendPages: state.appendPages,
    })),
  );

  const viewStateRef = useRef<{
    initializing: boolean;
    loadingMore: boolean;
    currentIndexPageType: PageType | null;
    activeViewRequestId: number;
    pendingClickedIndexPageType: PageType | null;
    suppressedAutoOpenPageId: string | null;
  }>({
    initializing: false,
    loadingMore: false,
    currentIndexPageType: null,
    activeViewRequestId: 0,
    pendingClickedIndexPageType: null,
    suppressedAutoOpenPageId: null,
  });

  const currentPage = page.props.page as Page | null | undefined;
  const currentPageId = currentPage?.id;
  const currentPageType = currentPage?.pageType;

  const beginViewRequest = useCallback((indexPageType: PageType | null) => {
    const state = viewStateRef.current;
    state.activeViewRequestId += 1;
    state.initializing = false;
    state.loadingMore = false;
    state.currentIndexPageType = indexPageType;
    return state.activeViewRequestId;
  }, []);

  const isCurrentViewRequest = useCallback(
    (requestId: number, expectedIndexType: PageType) =>
      viewStateRef.current.activeViewRequestId === requestId &&
      viewStateRef.current.currentIndexPageType === expectedIndexType,
    [],
  );

  const clearNestedView = useCallback(() => {
    beginViewRequest(null);
    viewStateRef.current.pendingClickedIndexPageType = null;
    if (activeNestedView !== null) {
      setActiveNestedView(null);
    }
  }, [activeNestedView, beginViewRequest, setActiveNestedView]);

  const buildIndexPageView = useCallback(
    (
      indexPageType: PageType,
      indexPage: Page | undefined,
      overrides: Partial<ActivePageTypeView> = {},
    ): ActivePageTypeView => ({
      pageId: indexPage?.id ?? indexPageType,
      pageTitle: getIndexPageLabel(indexPageType) ?? indexPage?.title ?? indexPageType,
      pageType: indexPageType,
      indexPage,
      children: [],
      isIndexPage: true,
      pagination: createDefaultPaginationInfo(),
      ...overrides,
    }),
    [],
  );

  const loadIndexPageView = useCallback(
    (indexPageType: PageType, indexPage: Page | undefined, showLoadingInitial: boolean) => {
      const requestId = beginViewRequest(indexPageType);
      viewStateRef.current.initializing = true;

      if (showLoadingInitial) {
        setActiveNestedView(
          buildIndexPageView(indexPageType, indexPage, { isLoadingInitial: true }),
        );
      }

      fetchIndexPageChildren(indexPageType)
        .then((result) => {
          if (!isCurrentViewRequest(requestId, indexPageType)) {
            return;
          }
          setActiveNestedView(
            buildIndexPageView(indexPageType, indexPage, {
              children: result.pages,
              pagination: result.pagination,
              isLoadingInitial: false,
              searchQuery: '',
            }),
          );
        })
        .catch((error: unknown) => {
          if (!isCurrentViewRequest(requestId, indexPageType)) {
            return;
          }
          console.error(`Failed to fetch ${indexPageType} children:`, error);
          toast.error(extractApiErrorMessage(error) ?? 'Failed to load pages');
          setActiveNestedView(
            buildIndexPageView(indexPageType, indexPage, {
              children: [],
              isLoadingInitial: false,
              searchQuery: '',
            }),
          );
        })
        .finally(() => {
          if (isCurrentViewRequest(requestId, indexPageType)) {
            viewStateRef.current.initializing = false;
          }
        });
    },
    [beginViewRequest, buildIndexPageView, isCurrentViewRequest, setActiveNestedView],
  );

  useEffect(() => {
    const allPages = [...mainNavigationTree, ...nonNavigationTree];

    if (!isSidebarActive) {
      viewStateRef.current.suppressedAutoOpenPageId = null;
      clearNestedView();
      return;
    }

    if (!hasLoaded || !allPages.length) {
      clearNestedView();
      return;
    }

    // `page` can be briefly undefined mid-Inertia visit while the sidebar remains active.
    if (!currentPageType) {
      return;
    }

    const indexPageType = isIndexPageType(currentPageType)
      ? currentPageType
      : CHILD_TO_INDEX_TYPE[currentPageType];

    const pendingClickedIndexPageType = viewStateRef.current.pendingClickedIndexPageType;
    if (pendingClickedIndexPageType && pendingClickedIndexPageType !== indexPageType) {
      return;
    }

    if (!indexPageType || !hasNestedPageView(indexPageType)) {
      viewStateRef.current.suppressedAutoOpenPageId = null;
      clearNestedView();
      return;
    }

    if (pendingClickedIndexPageType) {
      viewStateRef.current.pendingClickedIndexPageType = null;
    }

    if (viewStateRef.current.suppressedAutoOpenPageId) {
      if (viewStateRef.current.suppressedAutoOpenPageId === currentPageId) {
        return;
      }
      viewStateRef.current.suppressedAutoOpenPageId = null;
    }

    const indexPage =
      (currentPage?.pageType === indexPageType ? currentPage : undefined) ??
      findPageByType(allPages, indexPageType) ??
      indexPageFromEditorPage(currentPage, indexPageType);

    if (activeNestedView?.pageType === indexPageType && !activeNestedView.isLoadingInitial) {
      viewStateRef.current.currentIndexPageType = indexPageType;
      return;
    }

    const { initializing, currentIndexPageType: inFlightType } = viewStateRef.current;
    if (inFlightType === indexPageType && initializing) {
      return;
    }

    loadIndexPageView(indexPageType, indexPage, activeNestedView?.pageType !== indexPageType);
  }, [
    isSidebarActive,
    hasLoaded,
    currentPage,
    currentPageId,
    currentPageType,
    mainNavigationTree,
    nonNavigationTree,
    clearNestedView,
    activeNestedView,
    loadIndexPageView,
  ]);

  const resolvedActiveNestedView = (() => {
    if (!activeNestedView?.isIndexPage) {
      return activeNestedView;
    }

    // Prefer tree/store — Inertia page props stay stale after publish/delete.
    const resolvedIndexPage =
      findPageByType([...mainNavigationTree, ...nonNavigationTree], activeNestedView.pageType) ??
      activeNestedView.indexPage;

    return {
      ...activeNestedView,
      pageId: resolvedIndexPage?.id ?? activeNestedView.pageId,
      // Keep the section label ("Blog"), not the index page's editable title.
      indexPage: resolvedIndexPage,
    };
  })();

  const handleBackToPages = useCallback(() => {
    beginViewRequest(null);
    viewStateRef.current.pendingClickedIndexPageType = null;
    viewStateRef.current.suppressedAutoOpenPageId = currentPageId ?? null;
    setActiveNestedView(null);
  }, [currentPageId, beginViewRequest, setActiveNestedView]);

  const handlePageClick = useCallback(
    (clickedPage: Page) => {
      const pageType = clickedPage.pageType;
      if (!isIndexPageType(pageType) || !hasNestedPageView(pageType)) {
        return;
      }

      viewStateRef.current.pendingClickedIndexPageType = pageType;
      viewStateRef.current.suppressedAutoOpenPageId = null;

      if (activeNestedView?.pageType === pageType && !activeNestedView.isLoadingInitial) {
        viewStateRef.current.currentIndexPageType = pageType;
        return;
      }

      loadIndexPageView(pageType, clickedPage, true);
    },
    [activeNestedView?.isLoadingInitial, activeNestedView?.pageType, loadIndexPageView],
  );

  const handleSearchPages = useCallback(
    (searchQuery: string) => {
      if (!activeNestedView?.isIndexPage) return;

      const pageType = activeNestedView.pageType;
      const normalizedSearchQuery = searchQuery.trim();
      if (normalizedSearchQuery === (activeNestedView.searchQuery ?? '').trim()) return;

      const requestId = beginViewRequest(pageType);
      viewStateRef.current.initializing = true;

      fetchIndexPageChildren(pageType, DEFAULT_PAGE, DEFAULT_PER_PAGE, normalizedSearchQuery)
        .then((result) => {
          if (!isCurrentViewRequest(requestId, pageType)) return;
          setActiveNestedView({
            ...activeNestedView,
            children: result.pages,
            pagination: result.pagination,
            searchQuery: searchQuery,
          });
        })
        .catch((error: unknown) => {
          if (!isCurrentViewRequest(requestId, pageType)) return;
          console.error(`Failed to search ${pageType} children:`, error);
          toast.error(extractApiErrorMessage(error) ?? 'Failed to search pages');
        })
        .finally(() => {
          if (isCurrentViewRequest(requestId, pageType)) {
            viewStateRef.current.initializing = false;
          }
        });
    },
    [activeNestedView, beginViewRequest, isCurrentViewRequest, setActiveNestedView],
  );

  const handleLoadMorePages = useCallback(() => {
    if (!activeNestedView?.isIndexPage) return Promise.resolve();
    if (viewStateRef.current.loadingMore) return Promise.resolve();

    const requestId = viewStateRef.current.activeViewRequestId;
    const pageTypeForRequest = activeNestedView.pageType;
    viewStateRef.current.loadingMore = true;

    const nextPage = (activeNestedView.pagination?.currentPage || DEFAULT_PAGE) + 1;

    return fetchIndexPageChildren(
      pageTypeForRequest,
      nextPage,
      DEFAULT_PER_PAGE,
      activeNestedView.searchQuery ?? '',
    )
      .then((result) => {
        if (!isCurrentViewRequest(requestId, pageTypeForRequest)) {
          return;
        }
        appendPages(result.pages, result.pagination);
      })
      .catch((error: unknown) => {
        if (!isCurrentViewRequest(requestId, pageTypeForRequest)) {
          return;
        }
        console.error(`Failed to load more ${pageTypeForRequest} children:`, error);
        toast.error(extractApiErrorMessage(error) ?? 'Failed to load more pages');
      })
      .finally(() => {
        if (isCurrentViewRequest(requestId, pageTypeForRequest)) {
          viewStateRef.current.loadingMore = false;
        }
      });
  }, [
    appendPages,
    activeNestedView?.isIndexPage,
    activeNestedView?.pagination?.currentPage,
    activeNestedView?.pageType,
    activeNestedView?.searchQuery,
    isCurrentViewRequest,
  ]);

  return {
    activeNestedView: resolvedActiveNestedView,
    handleBackToPages,
    handlePageClick,
    handleLoadMorePages,
    handleSearchPages,
  };
}
